import {config} from '../../Config';
import {BaseService} from './BaseService';

import {ObjectUtils} from "../../Utils";

export class BaseCachedService extends BaseService {
    constructor() {
        super();

        this.cachedValue = null;
        this.resolving = false;

        // The old subscription never unsubscribed so I'm not sure if we need
        // to unsubscribe this.
        this.userSubscription = config.getStore().subscribe(() => this.HandleStoreUpdate());
    }

    get TitleProperty() { return "name"; }

    get cached() {
        return this.cachedValue !== null;
    }
    get cachedValue() {
        return ObjectUtils.Clone(this._cachedValue);
    }
    
    set cachedValue(newValue) {
        this._cachedValue = newValue;
    }

    get resolving() {
        return this._resolving;
    }
    set resolving(x) {
        this._resolving = x;
    }

    get pendingPromises() {
        if(!this._pendingPromises) {
            this._pendingPromises = [];
        }

        return this._pendingPromises;
    }
    set pendingPromises(x) {
        this._pendingPromises = x;
    }

    HandleStoreUpdate() {
        const user = config.getStore().getState().userState.user;
        if (user === null) {
            this.FlushCache();
        }
    }

    FlushCache() {
        this.cachedValue = null;
        this.resolving = false;
    }
    
    PrimeCache() {
        if(!this.cached) {
            return this.Get();
        }
    }
    
    GetWithContext() {
        return this.Get();
    }

    Get() {
        if(this.resolving) {
            let defer = {
                promise: null,
                resolve: null,
                reject: null
            };
            defer.promise = new Promise((resolve, reject) => { defer.resolve = resolve; defer.reject = reject; });

            this.pendingPromises.push(defer);

            return defer.promise;
        } else if(this.cached) {
            return new Promise((resolve) => { resolve(this.cachedValue); });
        } else {
            this.resolving = true;

            return new Promise((resolve, reject) => {
                this.SendRequest(BaseService.GET)
                    .then((x) => {
                        this.resolving = false;
                        this.cachedValue = x;

                        this.pendingPromises.forEach((y) => y.resolve(this.cachedValue));
                        resolve(this.cachedValue);

                        this.pendingPromises = null;
                    })
                    .catch((x) => {
                        this.resolving = false;

                        this.pendingPromises.forEach((y) => y.reject(ObjectUtils.Clone(x)));
                        reject(ObjectUtils.Clone(x));

                        this.pendingPromises = null;
                    })
            });
        }
    }

    // A synchronous method that, unlike FindById, will
    // return null if the value is not already cached
    CachedValueSearch(searchString) {
        if(this.cached && this.cachedValue?.result?.length > 0) {
            const terms = searchString.toLowerCase().split(' ');
            const titleProperty = this.TitleProperty;
            return {
                ...this.cachedValue,
                result: this.cachedValue.result.filter((x) => terms.every((term) => x[titleProperty].toLowerCase().includes(term)))
            };
        }
        return null;
    }

    Search(searchString) {
        return new Promise((resolve, reject) => {
            this.Get()
                .then((x) => {
                    resolve(this.CachedValueSearch(searchString));
                })
                .catch((x) => {
                    reject(ObjectUtils.Clone(x));
                })
        });
    }

    CachedValues() {
        return this.cached ? this.cachedValue : null;
    }

    // A synchronous method that, unlike FindById, will
    // return null if the value is not already cached
    CachedValueById(id) {
        if(id == null) { return null; }
        return this.CachedValueByProperty(id, this.PrimaryKeyProperty)
    }

    CachedValueByProperty(id, property) {
        if(this.cached && this.cachedValue?.result?.length > 0) {
            return {
                ... this.cachedValue,
                result: this.cachedValue.result.find((x) => String(x[property]) === String(id))
            }
        }
        return null;
    }
    
    CachedTitleById(id) {
        const model = this.CachedValueById(id)?.result;
        return model ? this.GetTitle(model) : null;
    }

    FindById(id) {
        return this.FindByProperty(id, this.PrimaryKeyProperty);
    }

    FindByCode(code) {
        return this.FindByProperty(code,'code');
    }

    FindByProperty(id, property) {
        const cachedValueForProperty = this.CachedValueByProperty(id, property);
        if(cachedValueForProperty) {
            return new Promise((resolve, _) => {
                resolve(cachedValueForProperty);
            });
        }

        return new Promise((resolve, reject) => {
            this.Get()
                .then((x) => {
                    resolve(this.CachedValueByProperty(id, property));
                })
                .catch((x) => {
                    reject(ObjectUtils.Clone(x));
                })
        });
    }
}

